import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { IoLogOut, IoMenu, IoClose } from 'react-icons/io5';
import { AuthContext } from '../context/AuthContext';

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Sidebar (mobile)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Live clock (IST)
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const currentTime = useMemo(
    () =>
      now.toLocaleString('en-US', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }),
    [now]
  );

  const handleLogout = () => {
    try {
      logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const initials = useMemo(() => {
    const fn = (user?.firstName || '').trim();
    const ln = (user?.lastName || '').trim();
    if (!fn && !ln) return 'U';
    const a = fn ? fn[0] : '';
    const b = ln ? ln[0] : '';
    return (a + b).toUpperCase() || 'U';
  }, [user?.firstName, user?.lastName]);

  const roleLabel =
    user?.userType === 'SUPER_ADMIN' ? 'Super Admin' : 'Company Admin';

  const navLinkClass = ({ isActive }) =>
    `block px-4 py-2 rounded-lg text-[15px] font-medium transition-colors duration-200 ${
      isActive
        ? 'bg-teal-600 text-white shadow'
        : 'text-gray-300 hover:bg-teal-600 hover:text-white'
    }`;

  return (
    <div className="flex h-screen bg-gray-50 text-gray-100 font-sans">
      {/* Mobile overlay */}
      <div
        onClick={() => setIsSidebarOpen(false)}
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden transition-opacity ${
          isSidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      />

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 box-border bg-slate-600 p-6 shadow-xl z-30 transform transition-transform duration-300
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:inset-auto lg:h-screen`}
        aria-label="Sidebar"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-teal-300 tracking-tight">
            Dashboard
          </h2>
          <button
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-700"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close navigation"
          >
            <IoClose className="text-xl" />
          </button>
        </div>

        {/* User card */}
        <div className="flex items-center gap-3 bg-gray-700/40 border border-gray-700 rounded-lg p-3 mb-6">
          <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="truncate font-semibold text-sm">
              {user?.firstName
                ? `${user.firstName}${user?.lastName ? ' ' + user.lastName : ''}`
                : 'User'}
            </div>
            <div className="text-xs text-teal-300">{roleLabel}</div>
          </div>
        </div>

        <nav>
          <ul className="space-y-4">
            {/* SUPER_ADMIN links */}
            {user?.userType === 'SUPER_ADMIN' && (
              <>
                <li className="text-xs uppercase tracking-wider text-teal-400/80 px-1">
                  Administration
                </li>
                <li>
                  <NavLink to="/dashboard/load-amount" className={navLinkClass}>
                    Load Amount
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/dashboard/company-balance"
                    className={navLinkClass}
                  >
                    Company Balance
                  </NavLink>
                </li>
              </>
            )}

            {/* COMPANY_ADMIN links */}
            {user?.userType !== 'SUPER_ADMIN' && (
              <>
                <li className="text-xs uppercase tracking-wider text-teal-400/80 px-1 mt-2">
                  Company
                </li>
                <li>
                  <NavLink
                    to="/dashboard/company-dashboard"
                    className={navLinkClass}
                  >
                    Balance Summary(Amount)
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/dashboard/user-points"
                    className={navLinkClass}
                  >
                    Points/Coins Summary
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/dashboard/user-points-ledger"
                    className={navLinkClass}
                  >
                    Points/Coins Ledger
                  </NavLink>
                </li>
              </>
            )}
          </ul>
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1  p-4 sm:p-6 bg-white text-gray-800 overflow-auto min-h-screen">
        {/* Top bar */}
        <header className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <IoMenu className="text-xl" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                Welcome, {user?.firstName || 'User'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">{currentTime}</p>
            </div>
          </div>
            <Link to="/dashboard/change-password">
            Change Password
            </Link>
          <button
            onClick={handleLogout}
            className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-md"
          >
            <IoLogOut className="mr-2 text-lg" /> Logout
          </button>
        </header>

        {/* Routed content */}
        <div className="bg-white p-4 sm:p-6 w-full rounded-lg shadow-lg">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
